import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { insumosService } from '../api/insumos';
import OnboardingModal from '../components/OnboardingModal';
import { usePermissions } from '../hooks/usePermissions';
import useDashboardStore from '../store/dashboardStore';
import toast from 'react-hot-toast';

import { citasService } from '../api/citas';
import api from '../api/axios';
import { format, differenceInYears, parseISO } from 'date-fns';
import es from 'date-fns/locale/es';
import { formatInTimeZone } from 'date-fns-tz';
import { Calendar, Users, Activity, TrendingUp, AlertTriangle } from 'lucide-react';
import DateRangeSelector from '../components/ui/DateRangeSelector';

// Zona horaria del cliente (Chile continental — UTC-3/-4, sin DST desde 2016)
// Para zonas adicionales agregar aquí; usar Intl.DateTimeFormat para detectar automáticamente
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Santiago';

// Colores EstetikFlow (Vitalidad Serena)
const COLORS = {
  coral: '#E76F51',
  nude: '#E9C46A',
  tealLight: '#3DB5A6',
  tealDark: '#227f74'
};

// Componente KPI Card
const KPICard = ({ icon: Icon, title, value, subtitle, color = 'teal' }) => {
  const colorClasses = {
    teal: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',
    coral: 'bg-[#E76F51]/10 text-[#E76F51]',
    nude: 'bg-[#E9C46A]/10 text-[#D4A843]',
    blue: 'bg-blue-100 text-blue-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};



// Componente EstadoSelector - Dropdown interactivo que parece un badge
const EstadoSelector = ({ cita, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const estados = [
    { value: 'reservada', label: '📅 Reservada', bgClass: 'bg-amber-100', textClass: 'text-amber-800', hoverClass: 'hover:bg-amber-50' },
    { value: 'confirmada', label: '✅ Confirmada', bgClass: 'bg-blue-100', textClass: 'text-blue-800', hoverClass: 'hover:bg-blue-50' },
    { value: 'completada', label: '🎉 Completada', bgClass: 'bg-green-100', textClass: 'text-green-800', hoverClass: 'hover:bg-green-50' },
    { value: 'cancelada', label: '❌ Cancelada', bgClass: 'bg-red-100', textClass: 'text-red-800', hoverClass: 'hover:bg-red-50' },
  ];

  const estadoActual = estados.find(e => e.value === cita.estado) || estados[0];

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = async (nuevoEstado) => {
    if (nuevoEstado === cita.estado) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      await citasService.update(cita.id, { estado: nuevoEstado });
      onUpdate(); // Recargar datos del dashboard
      
      // Si se completó, mostrar modal para crear ficha
      if (nuevoEstado === 'completada') {
        setShowRedirectModal(true);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast.error('Error al actualizar el estado de la cita');
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  // Función para redirigir a fichas
  const handleGoToFicha = () => {
    setShowRedirectModal(false);
    navigate(`/app/fichas-clinicas?nuevo=true&cita_id=${cita.id}&paciente_rut=${cita.paciente_rut || ''}&tratamiento_id=${cita.tratamiento_id || ''}`);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Badge clickeable */}
      <button
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className={`px-3 py-1 text-xs font-semibold rounded-full cursor-pointer transition-all
          ${estadoActual.bgClass} ${estadoActual.textClass}
          ${loading ? 'opacity-50 cursor-wait' : 'hover:ring-2 hover:ring-offset-1 hover:ring-gray-300'}
        `}
      >
        {loading ? (
          <span className="flex items-center gap-1">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            ...
          </span>
        ) : (
          <>
            {estadoActual.label.split(' ')[0]} {cita.estado.charAt(0).toUpperCase() + cita.estado.slice(1)}
            <svg className="inline-block ml-1 w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown flotante */}
      {isOpen && (
        <div 
          className="fixed w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-[100] py-1"
          style={{
            top: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 4 : 0,
            left: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().right - 160 : 0,
          }}
        >
          {estados.map((estado) => (
            <button
              key={estado.value}
              onClick={() => handleChange(estado.value)}
              disabled={estado.value === cita.estado}
              className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2
                ${estado.value === cita.estado 
                  ? `${estado.bgClass} ${estado.textClass} font-semibold` 
                  : `text-gray-700 ${estado.hoverClass}`
                }
              `}
            >
              <span>{estado.label}</span>
              {estado.value === cita.estado && (
                <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Modal de Redirección a Ficha */}
      {showRedirectModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[60] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowRedirectModal(false)}>
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header Verde */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ¡Cita Finalizada!
              </h3>
            </div>
            
            <div className="p-6">
              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
                <div className="flex gap-3">
                  <svg className="w-6 h-6 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-blue-800 mb-1">Registrar Insumos Utilizados</p>
                    <p className="text-sm text-blue-700">
                      El estado se actualizó correctamente. ¿Deseas crear la ficha clínica ahora para registrar los insumos utilizados?
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Info de la cita */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Paciente</p>
                    <p className="font-semibold">{cita.paciente_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Tratamiento</p>
                    <p className="font-semibold">{cita.tipo_tratamiento || cita.tratamiento_nombre}</p>
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRedirectModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Más tarde
                </button>
                <button
                  onClick={handleGoToFicha}
                  className="flex-1 px-4 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#227f74] font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Sí, ir a Ficha
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Estado vacío amigable
const EmptyState = () => (
  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2A9D8F]/10 mb-4">
      <TrendingUp className="w-8 h-8 text-[#2A9D8F]" />
    </div>
    <h3 className="text-xl font-semibold text-gray-800 mb-2">
      ¡Bienvenido a tu Dashboard!
    </h3>
    <p className="text-gray-500 max-w-md mx-auto">
      Aún no hay datos suficientes para mostrar estadísticas. 
      ¡Crea tu primera cita para empezar a ver métricas aquí!
    </p>
  </div>
);

const DashboardPage = () => {
  const [citasHoy, setCitasHoy] = useState([]);
  const [insumosCriticos, setInsumosCriticos] = useState([]);
  const [stats, setStats] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [statsRefreshing, setStatsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Store global de fechas
  const { selectedDate, viewMode, getDateRange } = useDashboardStore();
  
  // Permisos del plan
  const { canAccessInventory } = usePermissions();

  // Función para obtener el color según el estado de la cita
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'confirmada':
        return 'bg-primary/20 text-primary-dark';
      case 'completada':
        return 'bg-primary text-white';
      case 'cancelada':
        return 'bg-accent/20 text-accent';
      default:
        return 'bg-secondary text-gray-800';
    }
  };

  // Cargar estadísticas del dashboard con filtro del store
  const cargarStats = useCallback(async () => {
    try {
      setStatsRefreshing(true);
      
      const { startDate, endDate } = getDateRange();
      const params = new URLSearchParams();
      params.append('filter_type', viewMode);
      params.append('filter_value', startDate);
      
      const response = await api.get(`/dashboard/stats/?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setStatsRefreshing(false);
    }
  }, [viewMode, getDateRange]);

  // Función para procesar respuesta de citas
  const procesarCitas = useCallback((citasResponse, hoy) => {
    if (!citasResponse.data) return [];
    
    let lista = [];
    const payload = citasResponse.data;
    if (Array.isArray(payload)) {
      lista = payload;
    } else if (Array.isArray(payload?.results)) {
      lista = payload.results;
    } else if (Array.isArray(payload?.citas)) {
      lista = payload.citas;
    }

    return lista
      .filter(cita => cita.fecha === hoy)
      .map(cita => ({
        ...cita,
        hora: (cita.hora || '').substring(0, 5),
        tipo_tratamiento: cita.tratamiento_nombre || cita.tipo_tratamiento || 'Sin especificar',
        estado_color: getEstadoColor(cita.estado)
      }))
      .sort((a, b) => a.hora.localeCompare(b.hora));
  }, []);

  // Función para recargar datos
  const recargarDatos = useCallback(async () => {
    try {
      const timeZone = TIMEZONE;
      const hoy = formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd');
      
      const { startDate } = getDateRange();
      const params = new URLSearchParams();
      params.append('filter_type', viewMode);
      params.append('filter_value', startDate);
      
      const [citasResponse, statsResponse] = await Promise.all([
        citasService.getAll(),
        api.get(`/dashboard/stats/?${params.toString()}`)
      ]);
      
      // Fetch stock critico separately - may fail for inactive users
      let insumosData = [];
      try {
        const insumosResponse = await insumosService.getStockCritico();
        insumosData = insumosResponse.data || [];
      } catch (insumosError) {
        // Expected for users without inventory access
      }
      
      setCitasHoy(procesarCitas(citasResponse, hoy));
      setInsumosCriticos(insumosData);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error al recargar datos:', error);
    }
  }, [procesarCitas, viewMode, getDateRange]);

  // CARGA INICIAL
  useEffect(() => {
    const cargarTodoInicial = async () => {
      try {
        setError(null);
        
        const timeZone = TIMEZONE;
        const hoy = formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd');
        
        const { startDate } = getDateRange();
        const params = new URLSearchParams();
        params.append('filter_type', viewMode);
        params.append('filter_value', startDate);
        
        // Fetch citas and stats (always allowed)
        const [citasResponse, statsResponse] = await Promise.all([
          citasService.getAll(),
          api.get(`/dashboard/stats/?${params.toString()}`)
        ]);
        
        // Fetch stock critico separately - may fail for inactive users (403)
        let insumosData = [];
        try {
          const insumosResponse = await insumosService.getStockCritico();
          insumosData = insumosResponse.data || [];
        } catch (insumosError) {
          // Expected for users without inventory access - fail silently
        }
        
        setCitasHoy(procesarCitas(citasResponse, hoy));
        setInsumosCriticos(insumosData);
        setStats(statsResponse.data);
        
      } catch (error) {
        console.error('Error al cargar dashboard:', error);
        // Only show error if it's not an auth error (401 = user is logging out)
        if (error.response?.status !== 401) {
          setError('Error al cargar los datos del dashboard');
        }
      } finally {
        setInitialLoading(false);
      }
    };
    
    cargarTodoInicial();
    
    // Actualizar los datos cada 5 minutos
    const intervalo = setInterval(recargarDatos, 5 * 60 * 1000);
    
    return () => clearInterval(intervalo);
  }, [procesarCitas, recargarDatos, viewMode, getDateRange]);

  // Recargar stats cuando cambia la fecha o modo
  useEffect(() => {
    if (!initialLoading) {
      cargarStats();
    }
  }, [selectedDate, viewMode, cargarStats, initialLoading]);

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    try {
      const fechaNac = parseISO(fechaNacimiento);
      return differenceInYears(new Date(), fechaNac);
    } catch (error) {
      console.error('Error al calcular edad:', error, fechaNacimiento);
      return null;
    }
  };

  // Verificar si hay datos para mostrar estadísticas
  const hasData = stats && (
    stats.kpis?.total_citas_mes > 0 || 
    stats.kpis?.pacientes_nuevos > 0
  );

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-100px)] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A9D8F]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-gray-50 min-h-screen">
      <OnboardingModal />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
      </div>

      {/* Filtro de Fecha - Ahora sin props, conectado al store */}
      <DateRangeSelector className="mb-6" />
      
      {/* KPI Cards - Solo métricas operativas */}
      {!statsRefreshing && hasData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <KPICard
            icon={Calendar}
            title="Citas Totales"
            value={stats?.kpis?.total_citas_mes || 0}
            subtitle={stats?.meta?.periodo || 'Período seleccionado'}
            color="blue"
          />
          <KPICard
            icon={Users}
            title="Pacientes Nuevos"
            value={stats?.kpis?.pacientes_nuevos || 0}
            subtitle={stats?.meta?.periodo || 'Registrados'}
            color="nude"
          />
        </div>
      )}
      
      {/* SECCIÓN PRINCIPAL: Citas del día e Insumos Críticos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Citas del día */}
        <div className="bg-white p-5 rounded-xl shadow-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
            <h2 className="text-lg font-semibold text-gray-800">Citas del Día</h2>
            <p className="text-sm text-gray-500 capitalize">
              {format(new Date(), "EEEE d 'de' MMMM", { locale: es })}
            </p>
          </div>
          
          {citasHoy.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No hay citas programadas para hoy</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {citasHoy.map((cita) => (
                <div 
                  key={cita.id} 
                  className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{cita.paciente_nombre}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="font-medium text-[#2A9D8F]">{cita.hora}</span>
                        {' • '}
                        {cita.tipo_tratamiento}
                        {cita.paciente_fecha_nacimiento && (
                          <> • {calcularEdad(cita.paciente_fecha_nacimiento)} años</>
                        )}
                      </p>
                    </div>
                    <EstadoSelector cita={cita} onUpdate={recargarDatos} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Insumos críticos - Solo para planes con inventario */}
        {canAccessInventory && (
          <div className="bg-white p-5 rounded-xl shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Insumos con Stock Crítico
            </h2>
            {insumosCriticos.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <Activity className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No hay insumos con stock crítico</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {insumosCriticos.map((insumo) => (
                  <div 
                    key={insumo.id} 
                    className="border border-red-100 bg-red-50/30 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{insumo.nombre}</h3>
                        <p className="text-sm text-[#E76F51] mt-1">
                          Stock: {insumo.stock_actual} {insumo.unidad_medida}
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-[#E76F51]/10 text-[#E76F51] text-xs font-medium rounded-full">
                        Crítico: {insumo.stock_critico}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Si no tiene acceso a inventario, mostrar tarjeta placeholder */}
        {!canAccessInventory && (
          <div className="bg-white p-5 rounded-xl shadow-sm flex items-center justify-center">
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">Control de Inventario</h3>
              <p className="text-sm text-gray-400">Disponible en planes premium</p>
            </div>
          </div>
        )}
      </div>

      {/* Estado vacío si no hay datos */}
      {!hasData && !statsRefreshing && (
        <EmptyState />
      )}
    </div>
  );
};

export default DashboardPage;